import { useEffect, useState } from 'react';
import { User, Plus, Search, RefreshCw, Phone, ChevronRight, Filter } from 'lucide-react';
import { getDrivers, createDriver } from '../services/api';
import { Driver } from '../types';
import clsx from 'clsx';

export default function Drivers() {
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [newDriver, setNewDriver] = useState({ licenseNumber: '', driverName: '', mobile: '', transporterName: '' });

  const fetchDrivers = async () => {
    setLoading(true);
    try {
      const res = await getDrivers({ search: search || undefined, status: statusFilter || undefined, limit: 50 });
      if (res.success && res.data) setDrivers(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDrivers(); }, [search, statusFilter]);

  const handleAddDriver = async () => {
    if (!newDriver.licenseNumber || !newDriver.driverName) return alert('License number and name required');
    try {
      const res = await createDriver(newDriver);
      if (res.success) {
        setShowAddModal(false);
        setNewDriver({ licenseNumber: '', driverName: '', mobile: '', transporterName: '' });
        fetchDrivers();
      } else {
        alert(res.error || 'Failed to add driver');
      }
    } catch (err: any) {
      alert(err.message);
    }
  };

  const statusBadge = (status: string) => {
    const styles: Record<string, string> = {
      clean: 'bg-green-100 text-green-700',
      pending: 'bg-yellow-100 text-yellow-700',
      blocked: 'bg-red-100 text-red-700',
      unknown: 'bg-blue-100 text-blue-700',
    };
    return <span className={clsx('px-2 py-0.5 rounded text-xs font-medium', styles[status] || styles.unknown)}>{status.toUpperCase()}</span>;
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Drivers</h2>
          <p className="text-sm text-gray-500 mt-1">Manage drivers and monitor their challans</p>
        </div>
        <button onClick={() => setShowAddModal(true)} className="btn btn-primary flex items-center gap-2">
          <Plus className="w-4 h-4" /> Add Driver
        </button>
      </div>

      <div className="card p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by license number, name or mobile..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input pl-10 w-full"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-400" />
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="input w-auto">
              <option value="">All Status</option>
              <option value="clean">Clean</option>
              <option value="pending">Pending</option>
              <option value="blocked">Blocked</option>
            </select>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-primary-600" />
        </div>
      ) : drivers.length === 0 ? (
        <div className="card p-12 text-center">
          <User className="w-12 h-12 mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No drivers found</h3>
          <p className="text-gray-500">Add drivers to track their challans</p>
        </div>
      ) : (
        <div className="space-y-3">
          {drivers.map((driver) => (
            <div key={driver.licenseNumber} className="card p-4 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                    <User className="w-5 h-5 text-gray-500" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-gray-900">{driver.licenseNumber}</span>
                      {statusBadge(driver.compliance?.status || 'unknown')}
                    </div>
                    <p className="text-sm text-gray-500">{driver.driverName}</p>
                    {driver.mobile && (
                      <p className="text-xs text-gray-400 flex items-center gap-1 mt-0.5">
                        <Phone className="w-3 h-3" /> {driver.mobile}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-6">
                  <div className="text-right">
                    <p className="text-sm text-gray-500">Pending</p>
                    <p className="font-semibold text-warning-600">{driver.challanSummary?.pendingChallans || 0}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-500">Amount</p>
                    <p className="font-semibold text-gray-900">₹{(driver.challanSummary?.totalAmountPending || 0).toLocaleString()}</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-300" />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold mb-4">Add New Driver</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">License Number *</label>
                <input type="text" value={newDriver.licenseNumber} onChange={(e) => setNewDriver({...newDriver, licenseNumber: e.target.value})} className="input w-full" placeholder="MH1234567890123" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Driver Name *</label>
                <input type="text" value={newDriver.driverName} onChange={(e) => setNewDriver({...newDriver, driverName: e.target.value})} className="input w-full" placeholder="Full Name" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mobile</label>
                <input type="text" value={newDriver.mobile} onChange={(e) => setNewDriver({...newDriver, mobile: e.target.value})} className="input w-full" placeholder="9876543210" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Transporter</label>
                <input type="text" value={newDriver.transporterName} onChange={(e) => setNewDriver({...newDriver, transporterName: e.target.value})} className="input w-full" placeholder="Company Name" />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setShowAddModal(false)} className="btn btn-secondary">Cancel</button>
              <button onClick={handleAddDriver} className="btn btn-primary">Add Driver</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
